/*  UploadNameTransferModal.js  */
import React, { useState, useEffect } from "react";
import { Button } from "../../components/Utilities";
import { apiService } from "../../../services/apiService";
import "./UploadNameTransferModal.css";
import { Loading } from "../../components/Loading";

/* --------------------------------------------------  UTILS  -------------------------------------------------- */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      console.error("fileToBase64: Invalid file input:", file);
      return reject(new Error("Invalid file input: File object is required"));
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result?.split(",")[1];
      base64 ? resolve(base64) : reject(new Error("Failed to convert file to base64"));
    };
    reader.onerror = (err) => reject(err);
  });
};

/* --------------------------------------------------  COMPONENT  -------------------------------------------------- */
export const UploadNameTransferModal = ({
  isOpen,
  onClose,
  carId,
  onSubmitNameTransfer,
  rcData = null,     // <-- for view mode
  readOnly = false,  // <-- toggle view / edit
}) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------- Reset when modal closes ---------- */
  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isValid = !readOnly && files.length > 0;

  /* ---------- File selection ---------- */
  const handleFileChange = (e) => {
    if (readOnly) return;
    const selected = Array.from(e.target.files);
    setFiles(selected);
    setError(null);
  };

  /* ---------- Submit ---------- */
  const handleSubmit = async () => {
    if (readOnly) return;

    if (files.length === 0) {
      setError("Please upload at least one file.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const uploadedAt = new Date().toISOString();
      const fileUrls = [];

      for (const file of files) {
        const base64 = await fileToBase64(file);
        const ext =
          file.type === "application/pdf"
            ? "pdf"
            : file.type.includes("jpeg") || file.type.includes("jpg")
            ? "jpg"
            : "png";

        const fileName = `name_transfer_${carId}_${Date.now()}_RC.${ext}`;

        const payload = {
          file: base64,
          folder: "rc-documents",
          fileName,
          metadata: {
            contentType: file.type,
            originalName: file.name,
            size: file.size,
            carId,
            label: "New RC",
            uploadedAt,
          },
        };

        const res = await apiService.uploadFile(payload);
        fileUrls.push(res.data?.publicUrl || "");
      }

      const submitPayload = { carId, fileUrls };
      await apiService.submitNewRC(submitPayload);

      onSubmitNameTransfer?.(carId);
      setFiles([]);
      onClose();
    } catch (err) {
      console.error("RC submit error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to submit new RC. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------- PREVIEWS ---------- */
  const previews = readOnly
    ? (rcData?.fileUrls || []).map((url, i) => (
        <img
          key={i}
          src={url}
          alt="RC"
          className="preview-image"
          style={{ cursor: "pointer" }}
        />
      ))
    : files
        .filter((f) => f.type.startsWith("image/"))
        .map((f, i) => (
          <img
            key={i}
            src={URL.createObjectURL(f)}
            alt="RC preview"
            className="preview-image"
            style={{ cursor: "pointer" }}
          />
        ));

  /* --------------------------------------------------  RENDER  -------------------------------------------------- */
  return (
    <div className="name-transfer">
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="model-header">
            <p>{readOnly ? "View RC Book" : "Upload New RC Book"}</p>
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Body */}
          <div className="model-body">
            {loading && <Loading />}
            {error && <p className="error">{error}</p>}

            <div className="image-view">
              <div className="profile-pic">
                {previews.length > 0 ? (
                  previews
                ) : (
                  <p>No file uploaded</p>
                )}
              </div>

              {/* Upload button – hidden in view mode */}
              {!readOnly && (
                <>
                  <input
                    type="file"
                    id={`rcFileInput-${carId}`}
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="primary"
                    onClick={() =>
                      document.getElementById(`rcFileInput-${carId}`).click()
                    }
                  >
                    Upload New RC Book*
                  </Button>
                </>
              )}
            </div>

            {/* Submit button – only in edit mode */}
            {!readOnly && (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={loading || !isValid}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};